import { Alert, AlertColor, Slide, SlideProps, Snackbar } from '@mui/material';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { selectDarkMode } from '@store/ThemeSlice';

function SlideUp(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

interface ContextType {
  show: (message: string, severity: AlertColor) => void;
}

const Context = React.createContext<ContextType | null>(null);

const getAutoHideDuration = (sev: AlertColor): number => {
  switch (sev) {
    case 'success':
      return 4000;
    case 'info':
      return 5000;
    case 'warning':
      return 6000;
    case 'error':
      return 10000;
  }
};

const getVariant = (sev: AlertColor): 'outlined' | 'filled' => {
  switch (sev) {
    case 'success':
    case 'info':
      return 'outlined';
    case 'warning':
    case 'error':
      return 'filled';
  }
};


function ToastsProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const darkMode = useSelector(selectDarkMode);
  const [open, setOpen] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string>('');
  const [severity, setSeverity] = React.useState<AlertColor>('info');

  const show = React.useCallback(
    (messageToDisplay: string, messageSeverity: AlertColor) => {
      setMessage(messageToDisplay);
      setSeverity(messageSeverity);
      setOpen(true);
    },
    [],
  );

  const contextValue: ContextType = React.useMemo(() => ({ show }), [show]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    const isClickaway = reason === 'clickaway';
    const isImportant = severity === 'warning' || severity === 'error';

    if (isClickaway && isImportant) return;

    setOpen(false);
  };

  return (
    <Context.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={getAutoHideDuration(severity)}
        TransitionComponent={SlideUp}
        onClose={handleClose}
        data-testid="snackbar">
        <Alert
          severity={severity}
          variant={getVariant(severity)}
          data-testid={`snackbar-${severity}`}
          sx={
            getVariant(severity) === 'outlined'
              ? {
                  backgroundColor: darkMode ? '#1E293B' : 'background.paper',
                  color: darkMode ? '#F1F5F9' : 'text.primary',
                }
              : undefined
          }
          onClose={handleClose}>
          {message}
        </Alert>
      </Snackbar>
    </Context.Provider>
  );
}

export const useToastsContext = () => {
  const context = useContext(Context);

  if (context === null) {
    throw new Error('useToastsContext must be called by a child of ToastsProvider');
  }

  return context;
};

export default ToastsProvider;
